import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { getFieldValue } from '../../../contentful/mappers';
import './Hero.css';
import logo from '../../../assets/logo.png';
import Button from '../../Button/Button';

function Hero({ data }) {
  const logoRef = useRef(null);
  const logoContainerRef = useRef(null);
  const heroSectionRef = useRef(null);
  const [logoStyle, setLogoStyle] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  
  // Extract data after hooks
  const headline = data ? getFieldValue(data, 'headline') : null;
  const subheadline = data ? (getFieldValue(data, 'subheadline') || getFieldValue(data, 'subtitle')) : null;
  const ctaLabel = data ? getFieldValue(data, 'primaryCtaLabel') : null;
  const ctaUrl = data ? (getFieldValue(data, 'primaryCtaUrl') || getFieldValue(data, 'ctaLink')) : null;

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 900);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  useEffect(() => {
    const updateLogoPosition = () => {
      if (logoContainerRef.current) {
        const rect = logoContainerRef.current.getBoundingClientRect();
        const isMobileWidth = window.innerWidth <= 900;
        
        if (isMobileWidth) {
          // Mobile: center the logo - position it relative to the container
          const logoWidth = Math.min(200, window.innerWidth * 0.8);
          const isSmallMobile = window.innerWidth <= 600;
          const topOffset = isSmallMobile ? 100 : 120; // Lower position
          
          setLogoStyle({
            position: 'absolute',
            left: '50%',
            top: `${topOffset}px`, // Position from top of hero section
            width: `${logoWidth}px`,
            transform: 'translateX(-50%)',
            zIndex: 100,
            pointerEvents: 'none',
          });
        } else {
          // Desktop: use calculated position
          setLogoStyle({
            position: 'fixed',
            left: `${rect.left}px`,
            top: `${rect.top}px`,
            width: `${rect.width}px`,
            transform: 'none',
            zIndex: 100,
            pointerEvents: 'none',
          });
        }
      }
    };

    updateLogoPosition();
    window.addEventListener('resize', updateLogoPosition);
    window.addEventListener('scroll', updateLogoPosition);

    return () => {
      window.removeEventListener('resize', updateLogoPosition);
      window.removeEventListener('scroll', updateLogoPosition);
    };
  }, []);

  useEffect(() => {
    // Store the logo element in a way that Header can access it
    if (logoRef.current) {
      window.heroLogoElement = logoRef.current;
    }
    
    return () => {
      window.heroLogoElement = null;
    };
  }, [logoStyle]);

  // Early return after all hooks
  if (!data) return null;

  const portalLogo = logoStyle && typeof document !== 'undefined' ? (
    <div ref={logoRef} style={logoStyle} className="hero-logo-portal">
      <img src={logo} alt="KCGU logo" className="hero-logo-img" />
    </div>
  ) : null;

  const placeholderWidth = isMobile ? '200px' : '520px';

  return (
    <>
      <section ref={heroSectionRef} className="hero-section">
        {/* Render logo inside hero section on mobile so it scrolls with content */}
        {isMobile && portalLogo && portalLogo}
        <div className="container hero-grid">
          <div ref={logoContainerRef} className="hero-logo-col">
            {/* Placeholder to maintain layout */}
            <div style={{ width: placeholderWidth, height: '1px', opacity: 0 }} />
          </div>
          <div className="hero-content-col">
            {headline && <h1 className="hero-headline">{headline}</h1>}
            {subheadline && (
              <p className="hero-subheadline">{subheadline}</p>
            )}
            {ctaLabel && ctaUrl && (
              <Button href={ctaUrl} className="hero-cta">{ctaLabel}</Button>
            )}
          </div>
        </div>
      </section>
      {/* Render logo in portal on desktop to appear above navbar */}
      {!isMobile && typeof document !== 'undefined' && createPortal(portalLogo, document.body)}
    </>
  );
}

export default Hero;
